/**
 * /wiki/d/:docId — legacy canonical doc URL (Phase 5 Wave B).
 *
 * Post-Wave-B the canonical URL is `/wiki/{slug}-{8charTail}` — see
 * `src/routes/wiki/$slugWithTail.tsx`. This route stays mounted to
 * keep every existing deep link (browser bookmarks, email links,
 * embedded wiki references, notifications) working: it resolves the
 * node from the tree map and `replace`-navigates to the pretty URL.
 *
 * Folders mount `<FolderDetailPage>` directly — `FolderDetailPage`
 * reads its params via TanStack and the pretty-URL route is doc-only
 * for now, so the canonical URL for a folder stays `/wiki/d/:id`.
 *
 * Search param contract (Confluence-style new-doc flow):
 *   - `focusTitle=1` — `useCreateDocAndOpen` opens a new doc on its
 *     pretty URL directly and only lands here when no pretty path can be
 *     built; the param survives the redirect below. DocPage reads it via
 *     the loose `useSearch({ strict: false })` selector, auto-focuses the
 *     title input, and strips the param on whichever route it is on.
 */

import { createFileRoute, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useEffect } from 'react'

import { DocPage } from '@/features/wiki/components/DocPage'
import { FolderDetailPage } from '@/features/wiki/components/FolderDetailPage'
import { WikiDocSkeleton } from '@/features/wiki/components/WikiDocSkeleton'
import { useWikiTree } from '@/features/wiki/hooks/use-wiki-tree'
import { buildDocPath } from '@/features/wiki/lib/slug'

interface DocSearch {
  focusTitle?: '1'
}

/**
 * Legacy route shell that branches on the node's kind (Phase 5 Wave B):
 *
 *   - Doc kind → `replace`-navigate to the pretty `/wiki/{slug}-{tail}`
 *     URL. The redirect preserves the `focusTitle` search param so the
 *     new-doc-and-open flow's inline-title auto-focus still fires after
 *     the redirect. `<DocPage>` reads the param via the loose
 *     `useSearch({ strict: false })` selector on the destination route.
 *   - Folder kind → mount `<FolderDetailPage>` directly. The pretty
 *     route doesn't (yet) cover folders, so the canonical URL for a
 *     folder stays `/wiki/d/:id`.
 *
 * While the tree is loading on a deep-link refresh we render the
 * structural skeleton so the user sees the doc-page shape instead of
 * an empty pane.
 */
function DocOrFolder() {
  const params = useParams({ from: '/wiki/d/$docId/' }) as { docId: string }
  const search = useSearch({ from: '/wiki/d/$docId/' }) as DocSearch
  const navigate = useNavigate()
  const { nodesById, isLoading } = useWikiTree()
  const node = nodesById.get(params.docId)

  useEffect(() => {
    if (!node) return
    // Folders stay on this legacy route (FolderDetailPage reads params
    // directly). Docs AND file nodes both redirect to the pretty
    // `/wiki/{slug}-{tail}` URL — `buildDocPath` is kind-agnostic and the
    // pretty route branches to `<FileNodePage>` for file kinds. This is
    // also the route wiki-comment notification deep-links hit, so a
    // file-comment bell row lands on the file page (hash carried below).
    if (node.kind === 'folder') return
    void navigate({
      to: '/wiki/$slugWithTail',
      params: { slugWithTail: buildDocPath(node).replace(/^\/wiki\//, '') },
      // Preserve `focusTitle=1` across the redirect so the new-doc
      // inline-title auto-focus flow still fires on the destination
      // route. `<DocPage>`'s effect strips the param after consuming
      // it; an absent param renders as a normal doc view.
      search: search.focusTitle === '1' ? { focusTitle: '1' } : {},
      // Preserve any comment-deep-link hash (`#thread-<id>` /
      // `#page-comment-<id>`) across the redirect so a notification click
      // lands on the right thread. `<DocPage>` reads the hash on mount,
      // and without carrying it here the redirect would strip it.
      hash: typeof window !== 'undefined' ? window.location.hash.replace(/^#/, '') : undefined,
      replace: true,
    })
  }, [node, navigate, search.focusTitle])

  if (isLoading && !node) {
    return <WikiDocSkeleton />
  }
  if (node?.kind === 'folder') {
    return <FolderDetailPage />
  }
  // Node was not found in the tree (deleted, ACL-hidden, or never
  // existed). Render the skeleton briefly while the doc fetch + 404
  // surface in `<DocPage>` take over — `<DocPage>` itself owns the
  // empty / not-found state.
  if (!node) {
    return <DocPage />
  }
  // Doc OR file kind — the effect above is in flight redirecting to the
  // pretty URL. Render the skeleton so the user sees structure instead of
  // an empty flash during the redirect tick.
  return <WikiDocSkeleton />
}

export const Route = createFileRoute('/wiki/d/$docId/')({
  component: DocOrFolder,
  validateSearch: (search: Record<string, unknown>): DocSearch => ({
    // Only accept the literal '1'; anything else collapses to undefined
    // so a malformed inbound deep link doesn't keep the input in
    // permanently-focused state.
    focusTitle: search.focusTitle === '1' ? '1' : undefined,
  }),
})
